import React, { useMemo } from 'react';
import { Button, Flex, Text } from '@skin-seoul/design-system';
import { CLASSNAME, Country, CountryCurrency, formatCurrency, getCouponDisplayName } from '@skin-seoul/service';
import type { CartCouponResponse } from '@skin-seoul/service';
import { Condition } from '@skin-seoul/utils';

interface PriceCalculatorProps {
  subtotal: number;
  deliveryFee: number;
  totalPrice: number;
  totalTax: number;
  coupons: CartCouponResponse[];
  isFreeShipping?: boolean;
  country: Country;
  itemCount?: number;
  isDetailsVisible: boolean;
  onDetailsVisibleChange: (visible: boolean) => void;
}

const CartOrderSummary = ({
  subtotal,
  deliveryFee,
  totalPrice,
  totalTax,
  coupons,
  isFreeShipping = false,
  country,
  itemCount,
  isDetailsVisible,
  onDetailsVisibleChange,
}: PriceCalculatorProps) => {
  const currency = CountryCurrency[country];
  const formattedSubtotal = formatCurrency(currency, subtotal);
  const formattedDeliveryFee = formatCurrency(currency, deliveryFee);
  const formattedTotalPrice = formatCurrency(currency, totalPrice);
  const formattedTotalTax = formatCurrency(currency, totalTax);

  const totalCouponDiscount = coupons.reduce((sum, c) => sum + c.discount, 0);
  const couponDiscountPercent =
    subtotal > 0 && totalCouponDiscount > 0 ? Math.round((totalCouponDiscount / subtotal) * 100) : 0;

  const displayCoupons = useMemo(
    () =>
      coupons.map((coupon) => {
        const isBmsmCoupon = coupon.code.toLowerCase().startsWith('bmsm');
        const discountPercent =
          subtotal > 0 && coupon.discount > 0 ? Math.round((coupon.discount / subtotal) * 100) : 0;
        return {
          ...coupon,
          label: isBmsmCoupon ? 'BMSM Coupon' : getCouponDisplayName(coupon.code),
          discountPercent,
        };
      }),
    [coupons, subtotal],
  );

  const handleToggleDetails = () => {
    onDetailsVisibleChange(!isDetailsVisible);
  };

  return (
    <Flex flexDirection="column" gap={2}>
      {isDetailsVisible && (
        <Flex flexDirection="column" gap={2} backgroundColor="primary.50" padding={3} borderRadius="8px">
          <Flex alignItems="center" justifyContent="space-between">
            <Text textStyle="label.medium" fontWeight="regular" color="neutral.500" className={CLASSNAME.NOT_TRANSLATE}>
              Subtotal
            </Text>
            <Text textStyle="label.medium" fontWeight="medium" color="neutral.700" className={CLASSNAME.NOT_TRANSLATE}>
              {formattedSubtotal}
            </Text>
          </Flex>
          {displayCoupons.length > 0 &&
            displayCoupons.map((coupon) => (
              <Flex alignItems="center" justifyContent="space-between" key={coupon.code}>
                <Text
                  textStyle="label.medium"
                  fontWeight="regular"
                  color="neutral.500"
                  className={CLASSNAME.NOT_TRANSLATE}
                >
                  {coupon.label}
                  {coupon.discountPercent > 0 && (
                    <Text as="span" textStyle="label.medium" fontWeight="medium" color="primary.600">
                      {` (${coupon.discountPercent}% off)`}
                    </Text>
                  )}
                </Text>
                <Text
                  textStyle="label.medium"
                  fontWeight="medium"
                  color="neutral.700"
                  className={CLASSNAME.NOT_TRANSLATE}
                >
                  -{formatCurrency(CountryCurrency[country], coupon.discount)}
                </Text>
              </Flex>
            ))}
          <Flex alignItems="center" justifyContent="space-between">
            <Text textStyle="label.medium" fontWeight="regular" color="neutral.500" className={CLASSNAME.NOT_TRANSLATE}>
              Delivery Fee
            </Text>
            {isFreeShipping || deliveryFee === 0 ? (
              <Text
                textStyle="label.medium"
                fontWeight="medium"
                color="neutral.700"
                textTransform="uppercase"
                className={CLASSNAME.NOT_TRANSLATE}
              >
                Free
              </Text>
            ) : (
              <Text
                textStyle="label.medium"
                fontWeight="medium"
                color="neutral.700"
                className={CLASSNAME.NOT_TRANSLATE}
              >
                {formattedDeliveryFee}
              </Text>
            )}
          </Flex>
          <Condition
            expression={country === Country.UnitedStates}
            then={
              <Flex alignItems="center" justifyContent="space-between">
                <Text
                  textStyle="label.medium"
                  fontWeight="regular"
                  color="neutral.500"
                  className={CLASSNAME.NOT_TRANSLATE}
                >
                  Customs & taxes:
                </Text>
                <Text
                  textStyle="label.medium"
                  fontWeight="medium"
                  color="neutral.700"
                  className={CLASSNAME.NOT_TRANSLATE}
                >
                  {formattedTotalTax}
                </Text>
              </Flex>
            }
          />
        </Flex>
      )}
      <Flex alignItems="center" justifyContent="space-between" gap={1}>
        <Flex flexDirection="column" gap={1} flex={1} minWidth={0} alignItems="flex-start">
          <Text textStyle="label.medium" fontWeight="medium" color="neutral.800" className={CLASSNAME.NOT_TRANSLATE}>
            Order Total
            {itemCount != null && (
              <Text as="span" textStyle="label.small" fontWeight="regular" color="neutral.400">
                {' '}
                ({itemCount} items)
              </Text>
            )}
          </Text>
          <Button
            variant="plain"
            size="xs"
            padding={0}
            height="auto"
            minWidth={0}
            backgroundColor="transparent"
            textStyle="label.small"
            fontWeight="regular"
            color="neutral.400"
            textDecoration="underline"
            onClick={handleToggleDetails}
            className={CLASSNAME.NOT_TRANSLATE}
          >
            {isDetailsVisible ? 'Hide Details' : 'View Details'}
          </Button>
        </Flex>
        <Flex flexDirection="column" alignItems="flex-end" gap={1} flex={1} minWidth={0}>
          <Text
            textStyle="heading.h6.mobile"
            fontWeight="semibold"
            color="generic.100"
            className={CLASSNAME.NOT_TRANSLATE}
          >
            {formattedTotalPrice}
          </Text>
          {couponDiscountPercent > 0 && (
            <Flex gap={1} alignItems="center" justifyContent="flex-end">
              <Text
                textStyle="label.small"
                fontWeight="regular"
                color="neutral.400"
                textDecoration="line-through"
                className={CLASSNAME.NOT_TRANSLATE}
              >
                {formattedSubtotal}
              </Text>
              <Text textStyle="label.small" fontWeight="medium" color="primary.600" className={CLASSNAME.NOT_TRANSLATE}>
                {couponDiscountPercent}% off
              </Text>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default CartOrderSummary;
